import React from 'react';
import { BrowserRouter } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
// import { ReactQueryDevtools } from '@tanstack/react-query-devtools'; // Desactivado temporalmente

// Providers
import {
  ThemeProvider,
  TestingProvider,
  UserProvider,
  DataProvider,
  LeadsProvider,
  ClientsProvider,
  NavigationProvider,
  ProjectProvider,
  AICommandProvider,
  BackgroundUploadProvider
} from './shared/contexts';
import { MediaProvider } from './shared/contexts/MediaContext';
import { AuthProvider } from './shared/contexts/AuthContext';
import { TenantProvider } from './shared/contexts/TenantContext';
import { BulkSelectionProvider } from './shared/contexts/BulkSelectionContext';

// Components
import { ErrorBoundary, PWAInstaller } from './shared/components';
import { BackgroundUploadNotification } from './shared/components/ui/BackgroundUploadNotification';
import { QuoteViewingNotification } from './shared/components/ui/QuoteViewingNotification';
import { NotificationToast } from './shared/components/ui/NotificationToast';

// Router
import { AppRouter } from './routes/AppRouter';

// Config
import { queryClient } from './shared/config/queryClient';

// Styles
import './styles/tailwind.css';
import './styles/radar-view.css';

/**
 * App - Punto de entrada principal de la aplicación
 *
 * Arquitectura (Provider Hierarchy):
 * 1. ErrorBoundary - Captura de errores global
 * 2. QueryClient - React Query para data fetching
 * 3. AuthProvider - Autenticación y usuario
 * 4. TenantProvider - Contexto multi-tenant unificado (company/project/pipeline) ✨
 * 5. ThemeProvider - Dark/Light mode
 * 6. TestingProvider - Configuración de testing
 * 7. UserProvider - Estado de usuario
 * 8. DataProvider - Datos globales (legacy, en proceso de deprecación)
 * 9. MediaProvider - Gestión de multimedia
 * 10. AICommandProvider - Comandos AI
 * 11. LeadsProvider - Gestión específica de leads
 * 12. ClientsProvider - Gestión específica de clientes
 * 13. BrowserRouter - React Router v6
 * 14. ProjectProvider - Contexto de proyectos (legacy, usar TenantContext)
 * 15. NavigationProvider - Estado de navegación
 * 16. AppRouter - Rutas de la aplicación
 * 17. PWAInstaller - Progressive Web App
 */
let appRenderCount = 0;

function App() {
  appRenderCount++;

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <TenantProvider>
            <ThemeProvider defaultTheme="light" storageKey="owly-crm-theme">
              <TestingProvider>
                <UserProvider>
                  <DataProvider>
                    <MediaProvider>
                      <AICommandProvider>
                        <BackgroundUploadProvider>
                          <LeadsProvider>
                            <BulkSelectionProvider>
                              <ClientsProvider>
                                <BrowserRouter>
                                <ProjectProvider>
                                  <NavigationProvider>
                                    <AppRouter />
                                    {/* <PWAInstaller /> — Desactivado temporalmente */}
                                    <BackgroundUploadNotification />
                                    {/* QuoteViewingNotification desactivado - reemplazado por NotificationToast */}
                                    {/* <QuoteViewingNotification /> */}
                                    <NotificationToast />
                                    <Toaster position="top-right" />
                                  </NavigationProvider>
                                </ProjectProvider>
                                </BrowserRouter>
                              </ClientsProvider>
                            </BulkSelectionProvider>
                          </LeadsProvider>
                        </BackgroundUploadProvider>
                      </AICommandProvider>
                    </MediaProvider>
                  </DataProvider>
                </UserProvider>
              </TestingProvider>
            </ThemeProvider>
          </TenantProvider>
        </AuthProvider>

        {/* React Query DevTools - solo en desarrollo */}
        {/* Temporalmente desactivado para no interferir con OrbitView */}
        {/* {import.meta.env.DEV && (
          <ReactQueryDevtools
            initialIsOpen={false}
            position="bottom-right"
          />
        )} */}
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
